import { useCallback, useEffect, useState } from 'react';
import { apiService } from '../services/api';
import { LegalDocumentContent, LegalDocumentKey, UNAVAILABLE_LEGAL_DOCUMENT } from '../types';

/**
 * Loads one of the editable legal documents.
 *
 * Shared by the two public pages and by the consent box on the
 * registration form, so all three always show the same stored text
 * rather than three copies that can drift.
 *
 * On failure this resolves to UNAVAILABLE_LEGAL_DOCUMENT rather than
 * bundled text: a reader cannot tell a stale cached policy from the
 * current one, so saying nothing loaded is the honest answer. `failed`
 * is reported separately because the registration form has to do
 * something stronger with it than a page does — you should not be asked
 * to agree to a document that could not be shown to you.
 *
 * `reload` refetches. The registration form needs it when the server
 * rejects a consent as stale: the policy changed while the form was
 * open, and the applicant has to see the new wording before agreeing.
 */
export function useLegalDocument(doc: LegalDocumentKey) {
  const [content, setContent] = useState<LegalDocumentContent | null>(null);
  const [failed, setFailed] = useState(false);
  // Bumped to re-run the effect. Cheaper than duplicating the fetch in a
  // callback, and it keeps the cancellation logic in one place.
  const [reloadCount, setReloadCount] = useState(0);

  const reload = useCallback(() => setReloadCount((n) => n + 1), []);

  useEffect(() => {
    let active = true;
    setContent(null);
    setFailed(false);

    apiService
      .getLegalDocument(doc)
      .then((loaded) => {
        if (!active) return;
        setContent(loaded);
      })
      .catch((error) => {
        console.error('[useLegalDocument] Could not load', doc, error);
        if (!active) return;
        setContent(UNAVAILABLE_LEGAL_DOCUMENT);
        setFailed(true);
      });

    // The document key can change between renders (the editor switches
    // tabs), so a slow first response must not overwrite a fast second.
    return () => {
      active = false;
    };
  }, [doc, reloadCount]);

  return { content, failed, loading: content === null, reload };
}
